import React, { createContext, useContext, useEffect, useState } from 'react';
import { getUserPermissions, UserPermission } from '../services/settingsServices/userRolesService';
import { getUser } from '../services/settingsServices/usersService';
import { authProvider } from '../utils/authProvider';

interface UserPermissionsContextType {
    userPermissions: UserPermission | null;
    loading: boolean;
    isAdmin: boolean;
    isActive: boolean;
}

const UserPermissionsContext = createContext<UserPermissionsContextType | undefined>(undefined);

export const UserPermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const auth = authProvider();
    const userId = auth.userId;
    const [userPermissions, setUserPermissions] = useState<UserPermission | null>(null);
    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isActive, setIsActive] = useState(true);

    useEffect(() => {
        if (!userId) {
            console.warn('UserPermissionsContext: userId is null, skipping permissions fetch');
            setLoading(false);
            return;
        }

        const loadPermissions = async () => {
            try {
                console.log('UserPermissionsContext: fetching permissions for userId:', userId);
                const [permissions, user] = await Promise.all([
                    getUserPermissions(userId),
                    getUser(userId).catch(() => null),
                ]);
                setUserPermissions(permissions);
                if (user) {
                    const adminFlag = user.admin !== undefined
                        ? Boolean(user.admin)
                        : (user as any).adminInd === 1;
                    setIsAdmin(adminFlag);
                    const activeFlag = user.active !== undefined
                        ? Boolean(user.active)
                        : (user as any).statusInd === 1;
                    setIsActive(activeFlag);
                }
                console.log('UserPermissionsContext: permissions loaded:', permissions);
            } catch (error) {
                console.error('UserPermissionsContext: failed to load permissions:', error);
                setUserPermissions(null);
                setIsAdmin(false);
            } finally {
                setLoading(false);
            }
        };

        loadPermissions();
    }, [userId]);

    return (
        <UserPermissionsContext.Provider value={{ userPermissions, loading, isAdmin, isActive }}>
            {children}
        </UserPermissionsContext.Provider>
    );
};

export const useUserPermissions = () => {
    const context = useContext(UserPermissionsContext);
    if (context === undefined) {
        throw new Error('useUserPermissions must be used within a UserPermissionsProvider');
    }
    return context;
};
